import React from 'react';
import { useStore, Tab } from '../../stores';
import { X, Plus, Circle } from 'lucide-react';
import { cn, getMethodColor } from '../../lib/utils';
import { PresenceDot, usePresence } from '../presence';

export function TabBar() {
  const { tabs, activeTabId, openTab, closeTab, setActiveTab } = useStore();
  const { getUsersEditingRequest } = usePresence();

  const handleNewTab = () => {
    openTab();
  };

  return (
    <div className="h-8 flex items-center bg-muted/30 border-b border-border/50 overflow-x-auto">
      <div className="flex items-center min-w-0">
        {tabs.map((tab) => {
          const usersEditing = getUsersEditingRequest(tab.requestId);
          return (
            <TabItem
              key={tab.id}
              tab={tab}
              isActive={tab.id === activeTabId}
              onClick={() => setActiveTab(tab.id)}
              onClose={() => closeTab(tab.id)}
              usersEditing={usersEditing}
            />
          );
        })}
      </div>

      {/* New Tab Button */}
      <button
        onClick={handleNewTab}
        className="flex-shrink-0 p-1.5 hover:bg-accent rounded transition-colors mx-1"
        title="New Tab"
      >
        <Plus className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}

interface TabItemProps {
  tab: Tab;
  isActive: boolean;
  onClick: () => void;
  onClose: () => void;
  usersEditing: Array<{ userId: string; userName?: string; editingRequestId?: string }>;
}

function TabItem({ tab, isActive, onClick, onClose, usersEditing }: TabItemProps) {
  const methodColors: Record<string, string> = {
    GET: 'text-blue-400',
    POST: 'text-green-400',
    PUT: 'text-orange-400',
    PATCH: 'text-cyan-400',
    DELETE: 'text-red-400',
    HEAD: 'text-purple-400',
    OPTIONS: 'text-blue-600',
  };

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1 text-xs border-r border-border/50 cursor-pointer group min-w-0 max-w-[180px]',
        isActive
          ? 'bg-background text-foreground'
          : 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-background/50'
      )}
      onClick={onClick}
    >
      {/* Method Badge */}
      <span
        className={cn(
          'text-[10px] font-mono font-medium flex-shrink-0',
          methodColors[tab.request.method] || 'text-gray-400'
        )}
      >
        {tab.request.method}
      </span>

      {/* Tab Title */}
      <span className="truncate flex-1">
        {tab.title || 'New Request'}
      </span>

      {/* Presence Indicator - shows who else is editing this request */}
      {usersEditing.length > 0 && (
        <PresenceDot users={usersEditing} className="flex-shrink-0" />
      )}

      {/* Dirty Indicator */}
      {tab.isDirty && (
        <Circle className="w-2 h-2 fill-current text-muted-foreground flex-shrink-0" />
      )}

      {/* Close Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-accent transition-all flex-shrink-0"
        title="Close Tab"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
